import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PipelineStatus } from "@/components/admin/PipelineStatus";
import { PipelineStrip } from "@/components/admin/PipelineStrip";
import { WorkerPanel } from "@/components/admin/WorkerPanel";
import { ActivityFeed } from "@/components/admin/ActivityFeed";
import { AutopilotHealthCard } from "@/components/admin/AutopilotHealthCard";
import { PerformancePanel } from "@/components/admin/PerformancePanel";

import { getPipelineCounts } from "@/lib/dashboard.functions";
import {
  getScheduleSettings,
  updateScheduleSettings,
  triggerDiscovery,
  triggerAutopilotRefill,
  runPublishDue,
} from "@/lib/editorial.functions";
import { useRealtimeInvalidate } from "@/hooks/useRealtimeInvalidate";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/")({
  component: AdminDashboard,
});

function AdminDashboard() {
  const qc = useQueryClient();
  const { data: counts } = useQuery({
    queryKey: ["admin", "pipeline-counts"],
    queryFn: () => getPipelineCounts(),
    refetchInterval: 15000,
  });
  useRealtimeInvalidate([
    { table: "article_ideas", queryKey: ["admin", "pipeline-counts"] },
    { table: "article_generation_tasks", queryKey: ["admin", "pipeline-counts"] },
    { table: "articles", queryKey: ["admin", "pipeline-counts"] },
  ]);

  const { data: schedule } = useQuery({
    queryKey: ["admin", "schedule"],
    queryFn: () => getScheduleSettings(),
  });

  const toggleAuto = useMutation({
    mutationFn: (next: boolean) =>
      updateScheduleSettings({
        data: {
          articles_per_day: schedule?.articles_per_day ?? 2,
          publish_times: (schedule?.publish_times ?? ["09:00", "15:00"]).map((t: string) =>
            t.slice(0, 5),
          ),
          timezone: schedule?.timezone ?? "Europe/London",
          auto_publish: next,
          min_quality_score: schedule?.min_quality_score ?? 70,
        },
      }),
    onSuccess: (_r, v) => {
      toast.success(`Auto-publish ${v ? "on" : "off"}`);
      qc.invalidateQueries({ queryKey: ["admin", "schedule"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const discover = useMutation({
    mutationFn: () => triggerDiscovery({ data: { max: 15 } }),
    onSuccess: (r: any) =>
      toast.success(
        `Found ${r.ideasCreated} new ideas · queued ${r.refill?.queued ?? r.queuedDrafts ?? 0}`,
      ),
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  const refill = useMutation({
    mutationFn: () => triggerAutopilotRefill(),
    onSuccess: (r: any) => {
      if (r.queued > 0) toast.success(`Queued ${r.queued} idea(s) for drafting`);
      else toast.info(`Queue already stocked · ${r.activeSupply}/${r.targetBuffer}`);
    },
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  const publish = useMutation({
    mutationFn: () => runPublishDue(),
    onSuccess: (r: any) => {
      if (r.published === 0) toast.info("Nothing due.");
      else toast.success(`Published ${r.published}`);
    },
    onError: (e: any) => toast.error(e.message),
    onSettled: () => qc.invalidateQueries({ queryKey: ["admin", "pipeline-counts"] }),
  });

  // Build a single "what to do next" CTA from the live counts.
  const next = (() => {
    if (!counts) return null;
    if ((counts.ideas.new ?? 0) === 0 && (counts.ideas.approved ?? 0) === 0 && (counts.tasks.draftPending ?? 0) === 0 && (counts.articles.ready ?? 0) === 0) {
      return {
        title: "Start by running discovery",
        body: "Scan tier A–D sources for fresh local-SEO stories. Takes ~20s.",
        cta: "Run discovery now",
        action: () => discover.mutate(),
        pending: discover.isPending,
      };
    }
    if ((counts.ideas.new ?? 0) > 0) {
      return {
        title: `${counts.ideas.new} new ideas waiting for review`,
        body: "Approve the ones you want and send them to the writer.",
        cta: "Review ideas →",
        href: "/admin/ideas",
      };
    }
    if ((counts.ideas.approved ?? 0) + (counts.ideas.queued ?? 0) > 0) {
      return {
        title: `${(counts.ideas.approved ?? 0) + (counts.ideas.queued ?? 0)} ideas ready for drafting`,
        body: "Autopilot should queue these automatically; use refill if the worker is idle.",
        cta: "Refill queue now",
        action: () => refill.mutate(),
        pending: refill.isPending,
      };
    }
    if ((counts.tasks.draftPending ?? 0) + (counts.tasks.editPending ?? 0) + (counts.tasks.running ?? 0) > 0) {
      return {
        title: "Worker is processing the queue",
        body: "Drafts roll in roughly one per minute. Watch the worker panel for live updates.",
        cta: null,
      };
    }
    if ((counts.articles.ready ?? 0) > 0 && !schedule?.auto_publish) {
      return {
        title: `${counts.articles.ready} articles ready to publish`,
        body: "Turn on auto-publish or publish them manually.",
        cta: "Run publisher now",
        action: () => publish.mutate(),
        pending: publish.isPending,
      };
    }
    return {
      title: "You're on autopilot",
      body: `Next publish run within 15 min · cap ${schedule?.articles_per_day ?? 2}/day · min quality ${schedule?.min_quality_score ?? 70}.`,
      cta: "Discover more ideas",
      action: () => discover.mutate(),
      pending: discover.isPending,
    };
  })();

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="headline-display text-3xl">Newsroom</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Discover → Approve → Draft → Edit → Publish. Counts and the worker panel update live.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm border border-border px-3 py-2 bg-paper-soft">
          <input
            type="checkbox"
            checked={!!schedule?.auto_publish}
            onChange={(e) => toggleAuto.mutate(e.target.checked)}
          />
          Auto-publish
          <span className="text-xs text-muted-foreground">
            ({schedule?.articles_per_day ?? 2}/day · ≥{schedule?.min_quality_score ?? 70})
          </span>
        </label>
      </div>

      <PipelineStrip />

      <PipelineStatus />

      <AutopilotHealthCard />

      <PerformancePanel />

      {next && (
        <div className="border border-foreground/20 bg-paper-soft p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Next step</div>
            <h2 className="text-lg font-medium mt-1">{next.title}</h2>
            <p className="text-sm text-muted-foreground mt-1">{next.body}</p>
          </div>
          {next.cta &&
            (next.href ? (
              <Link to={next.href} className="bg-foreground text-background px-5 py-2 text-sm font-medium hover:bg-ochre-deep">
                {next.cta}
              </Link>
            ) : (
              <button
                onClick={next.action}
                disabled={next.pending}
                className="bg-foreground text-background px-5 py-2 text-sm font-medium hover:bg-ochre-deep disabled:opacity-50"
              >
                {next.pending ? "Working…" : next.cta}
              </button>
            ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <section>
          <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
            Live activity
          </h2>
          <ActivityFeed />
        </section>
        <section>
          <h2 className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
            Worker
          </h2>
          <WorkerPanel />
        </section>
      </div>


      <section className="grid sm:grid-cols-3 gap-3 text-sm">

        <Link to="/admin/ideas" search={{ status: "new" }} className="border border-border p-4 hover:bg-paper-soft">
          <div className="font-medium">Ideas backlog →</div>
          <div className="text-xs text-muted-foreground mt-1">Review discovered topics and send approved ones to the writer.</div>
        </Link>
        <Link to="/admin/generate" className="border border-border p-4 hover:bg-paper-soft">
          <div className="font-medium">Quick generate →</div>
          <div className="text-xs text-muted-foreground mt-1">Skip the backlog: queue N articles straight from top sources.</div>
        </Link>
        <Link to="/admin/schedule" className="border border-border p-4 hover:bg-paper-soft">
          <div className="font-medium">Schedule →</div>
          <div className="text-xs text-muted-foreground mt-1">Daily cap, quality threshold, auto-publish, pipeline view.</div>
        </Link>
      </section>
    </div>
  );
}
